// ============================================================================
// TAX CLASSIFICATION SERVICE (ADR-0047 Phase 2 §7) — the tenant-authored
// classification master of a jurisdiction (HSN / SAC under India GST) and the
// jurisdiction's statutory units. Reads resolve the entity's jurisdiction from
// X-Entity-ID unless a jurisdictionId is given.
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { taxClassificationEndpoints } from '../endpoints/tax.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, BackendPagedResult, PaginatedResponse, EmptyResponse, ID,
  TaxClassificationDto, CreateTaxClassificationRequest, UpdateTaxClassificationRequest,
  TaxClassificationQueryParams, TaxUnitDto,
} from '../types';

class TaxClassificationService {
  async getAll(params?: TaxClassificationQueryParams): Promise<PaginatedResponse<TaxClassificationDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(taxClassificationEndpoints.list(), params as Record<string, unknown>)
      : taxClassificationEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<TaxClassificationDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged?.items || []),
      pagination: {
        currentPage: paged?.page ?? 1, pageSize: paged?.pageSize ?? 25, totalPages: paged?.totalPages ?? 0,
        totalItems: paged?.totalCount ?? 0, hasNextPage: paged?.hasNext ?? false, hasPreviousPage: paged?.hasPrevious ?? false,
      },
    };
  }

  /** Search helper for pickers: codes and descriptions matching `term`, in force today. */
  async search(term: string, pageSize = 25): Promise<TaxClassificationDto[]> {
    const r = await this.getAll({ searchTerm: term || undefined, pageSize, asOf: new Date().toISOString().slice(0, 10) });
    return r.data;
  }

  async getById(id: ID): Promise<TaxClassificationDto> {
    const response = await get<ApiResponse<TaxClassificationDto>>(taxClassificationEndpoints.getById(id));
    return response.data.data;
  }

  async create(data: CreateTaxClassificationRequest): Promise<TaxClassificationDto> {
    const response = await post<ApiResponse<TaxClassificationDto>>(taxClassificationEndpoints.create(), data);
    return response.data.data;
  }

  async update(id: ID, data: UpdateTaxClassificationRequest): Promise<TaxClassificationDto> {
    const response = await put<ApiResponse<TaxClassificationDto>>(taxClassificationEndpoints.update(id), data);
    return response.data.data;
  }

  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const url = buildEndpointWithQuery(taxClassificationEndpoints.delete(id), { rowVersion });
    const response = await del<EmptyResponse>(url);
    return response.data;
  }

  /** The jurisdiction's statutory units in display order (empty for a regime that reports no quantity). */
  async getUnits(jurisdictionId?: string): Promise<TaxUnitDto[]> {
    const url = jurisdictionId
      ? buildEndpointWithQuery(taxClassificationEndpoints.units(), { jurisdictionId })
      : taxClassificationEndpoints.units();
    const response = await get<ApiResponse<TaxUnitDto[]>>(url);
    return response.data.data ?? [];
  }
}

export const taxClassificationService = new TaxClassificationService();
export default taxClassificationService;
